import React, { useRef } from 'react';

/**
 * Text input that only accepts integer / float values (like a number field).
 * On mobile, scrolls the field above the on-screen keyboard when focused.
 */
export default function NumberInput({
  value,
  onChange,
  allowDecimal = true,
  maxDecimals = 8,
  onFocus,
  ...props
}) {
  const inputRef = useRef(null);

  function handleChange(e) {
    let next = String(e.target.value || '');

    // Normalize commas to dots
    next = next.replace(/,/g, '.');

    // Strip invalid characters
    next = next.replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');

    if (allowDecimal) {
      const parts = next.split('.');
      if (parts.length > 2) {
        next = `${parts[0]}.${parts.slice(1).join('')}`;
      }
      const [whole, frac] = next.split('.');
      if (frac != null && maxDecimals >= 0) {
        next = `${whole}.${frac.slice(0, maxDecimals)}`;
      }
    }

    // Avoid multiple leading zeros like 00 → keep "0" or "0.x"
    if (/^0[0-9]/.test(next)) {
      next = next.replace(/^0+/, '0');
      if (next.startsWith('0') && next.length > 1 && next[1] !== '.') {
        next = next.replace(/^0+/, '');
      }
    }

    onChange?.(next);
  }

  function handleKeyDown(e) {
    if (['e', 'E', '+', '-'].includes(e.key)) {
      e.preventDefault();
    }
  }

  function handleFocus(e) {
    onFocus?.(e);
    const el = e.currentTarget;
    // Wait for mobile keyboard, then keep the field visible above it
    const scroll = () => {
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      } catch {
        el.scrollIntoView(true);
      }
    };
    requestAnimationFrame(scroll);
    setTimeout(scroll, 280);
    setTimeout(scroll, 500);
  }

  return (
    <input
      {...props}
      ref={inputRef}
      type="text"
      inputMode={allowDecimal ? 'decimal' : 'numeric'}
      pattern={allowDecimal ? '[0-9]*[.]?[0-9]*' : '[0-9]*'}
      value={value ?? ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      autoComplete="off"
      spellCheck={false}
    />
  );
}

/** Parse a validated numeric text value to Number, or NaN. */
export function parseNumberInput(value) {
  if (value === '' || value == null || value === '.') return NaN;
  return Number(value);
}
